import * as MapUtils from '../../common/map';
import { Actions as Acquisition } from '../../store/ducks/acquisition';
import { Actions as ShoreSModel } from '../../store/ducks/shoreSModel';
import { Actions as ShoreSAnalysis } from '../../store/ducks/shoreSAnalysis';
import { Actions as ShoreS } from '../../store/ducks/shoreS';
import { Actions as Map } from '../../store/ducks/map';
import { reprojectFeatureCollection } from './reprojection';
import { toDisplayFeatures } from './referenceExtraction';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'shore_s';

const REFERENCE_COASTLINE_GROUP = 'shoreSCoastlineData';
const REFERENCE_COLOR = '#d32f2f';

const PENDING_BASELINE_STORAGE_KEY = 'shoreSSessionImportBaseline';
const PENDING_REFERENCE_STORAGE_KEY = 'shoreSSessionImportReference';

/**
 * @function reviveDate
 * @description Turns an ISO date string (what `JSON.stringify` reduces a `Date` to) back into a
 * real `Date` — mirrors `shoreSAnalysis/saga.js`'s own `withDateTime` helper, since
 * `mergedDistances`/`modeledDistances`/`referenceDistances` rows carry `Date` fields that
 * `ShoreSErrorPlots`/`ShoreSExportDialog` expect, not strings.
 * @param {string|null} value - An ISO date string, or null.
 * **Called by:
 * @see {importSessionState}
 * @memberof module:ShoreS
 * @returns {Date|null}
 */
const reviveDate = (value) => (value ? new Date(value) : value);

/**
 * @function reviveDistanceRecords
 * @description Revives every record's `.time` field back into a `Date` — the shape
 * `modeledDistances`/`referenceDistances` carry.
 * @param {Array<Object>} records
 * **Called by:
 * @see {importSessionState}
 * @memberof module:ShoreS
 * @returns {Array<Object>}
 */
const reviveDistanceRecords = (records) => (records || []).map((r) => ({ ...r, time: reviveDate(r.time) }));

/**
 * @function reviveMergedRecords
 * @description Revives every merged row's `.time` AND `.timeReference` fields back into `Date`s —
 * the shape `mergedDistances` carries (see `algorithms/shoreS/merge.js`'s `mergeByTimeProximity`).
 * @param {Array<Object>} records
 * **Called by:
 * @see {importSessionState}
 * @memberof module:ShoreS
 * @returns {Array<Object>}
 */
const reviveMergedRecords = (records) =>
  (records || []).map((r) => ({ ...r, time: reviveDate(r.time), timeReference: reviveDate(r.timeReference) }));

/**
 * @function buildSessionExport
 * @description Bundles everything needed to redraw a completed Shore-S validation without
 * re-running it. Unlike the other modules, two things here are deliberately left to run for real
 * on import instead of being captured:
 * 1. The reference-coastline *extraction* (`state.shoreSAnalysis.extraction`) — it re-runs
 *    automatically via `useShoreSReferenceExtraction` as soon as `state.acquisition.satellite`,
 *    `state.shoreSModel.modelBBox` and `shoreSSelectedDates` are seeded (all three are, below), so
 *    there's no need to freeze/replay it — the same "let it recompute" call CSqueeze made for its
 *    median composites.
 * 2. The colored, per-transect-bias TRANSECT map layer — its geometry only ever exists as a local
 *    saga variable inside `handleRun` (never dispatched to Redux), and `transectsStep`/`extent`
 *    aren't persisted anywhere either, so redrawing it would mean re-running part of the pipeline
 *    (an Earth Engine call) rather than a cheap client-side replay. Skipped for this pass — the
 *    metrics/plots/export panel (the actual result being tested) don't depend on it at all.
 * @param {Object} params
 * @param {Number} params.satelliteIndex - `state.acquisition.satelliteIndex`.
 * @param {Object} params.model - `state.shoreSModel` (the parsed upload — crs/bbox/coastlines).
 * @param {Object} params.analysis - `state.shoreSAnalysis` (its `extraction`/`loading`/`error`
 * fields are ignored here; everything else is the validation run's own already-evaluated result).
 * @param {Object} params.images - `state.imagery.images`, keyed by numeric image id — the images
 * the user actually loaded as raster layers on the map (a subset of `selectedImages`, which is
 * just the candidate list). Without this, `importSessionState` has no way to know which images to
 * re-acquire, so the "Loaded Images" accordion comes back empty after an import.
 * **Called by:
 * @see {ProcessingShoreSPage} - Shore-S's result page, via the "export session for testing" icon.
 * @memberof module:ShoreS
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({ satelliteIndex, model, analysis, images }) => ({
  version: SESSION_EXPORT_VERSION,
  module: SESSION_EXPORT_MODULE,
  exportedAt: new Date().toISOString(),
  satelliteIndex,
  satelliteType: window.sessionStorage.getItem('typeSatellite') || 'TOA',
  selectedImages: JSON.parse(window.sessionStorage.getItem('shoreSSelectedDates') || '[]'),
  loadedImages: Object.values(images || {}).map((image) => ({
    missionName: image.missionName,
    date: image.date,
  })),
  model: {
    crs: model?.crs || null,
    hasTemporalData: Boolean(model?.hasTemporalData),
    dateRange: model?.dateRange || null,
    roiBBox: model?.roiBBox || null,
    modelBBox: model?.modelBBox || null,
    modeledCoastlinesGeoJSON: model?.modeledCoastlinesGeoJSON || null,
    modeledCoastlinesDisplayGeoJSON: model?.modeledCoastlinesDisplayGeoJSON || null,
  },
  baselineCoords: analysis?.baselineCoords || null,
  referenceCoastlinesGeoJSON: analysis?.referenceCoastlinesGeoJSON || null,
  modeledDistances: analysis?.modeledDistances || null,
  referenceDistances: analysis?.referenceDistances || null,
  mergedDistances: analysis?.mergedDistances || null,
  errorMetrics: analysis?.errorMetrics || null,
  aggregatedMetrics: analysis?.aggregatedMetrics || null,
  measuredDeviation: analysis?.measuredDeviation || null,
});

/**
 * @function importSessionState
 * @description Rehydrates a session exported by `buildSessionExport` straight into
 * `state.acquisition`/`state.shoreSModel`/`state.shoreSAnalysis`, and stashes the baseline/
 * reference-coastline data in sessionStorage for `restorePendingBaseline`/
 * `renderPendingReferenceLayer` to turn into map overlays once the result page's map exists. No
 * Earth Engine calls are made along this path.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Object} data - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * **Called by:
 * @see {NavigationBar} - via the standardized "import session for testing" icon's onImport.
 * @memberof module:ShoreS
 * @returns {void}
 */
export const importSessionState = (dispatch, data) => {
  dispatch(Acquisition.reset());
  const {
    satelliteIndex,
    satelliteType,
    selectedImages,
    loadedImages,
    model,
    baselineCoords,
    referenceCoastlinesGeoJSON,
    modeledDistances,
    referenceDistances,
    mergedDistances,
    errorMetrics,
    aggregatedMetrics,
    measuredDeviation,
  } = data;

  dispatch(Acquisition.setSatellite(satelliteIndex, satelliteType || 'TOA'));
  dispatch(ShoreSModel.setModel(model || {}));

  window.sessionStorage.setItem('shoreSSelectedDates', JSON.stringify(selectedImages || []));

  (loadedImages || []).forEach(({ missionName, date }) => {
    dispatch(ShoreS.acquireImage(missionName, date));
  });

  if (referenceCoastlinesGeoJSON && mergedDistances) {
    dispatch(
      ShoreSAnalysis.setResult({
        baselineCoords,
        referenceCoastlinesGeoJSON,
        modeledDistances: reviveDistanceRecords(modeledDistances),
        referenceDistances: reviveDistanceRecords(referenceDistances),
        mergedDistances: reviveMergedRecords(mergedDistances),
        errorMetrics,
        aggregatedMetrics,
        measuredDeviation,
      }),
    );
  }

  if (baselineCoords && baselineCoords.length >= 2) {
    window.sessionStorage.setItem(PENDING_BASELINE_STORAGE_KEY, JSON.stringify(baselineCoords));
  } else {
    window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);
  }

  if (referenceCoastlinesGeoJSON && model?.crs) {
    window.sessionStorage.setItem(
      PENDING_REFERENCE_STORAGE_KEY,
      JSON.stringify({ referenceCoastlinesGeoJSON, crs: model.crs }),
    );
  } else {
    window.sessionStorage.removeItem(PENDING_REFERENCE_STORAGE_KEY);
  }
};

/**
 * @function restorePendingBaseline
 * @description If `importSessionState` left a baseline waiting, draws it as a real
 * `google.maps.Polyline` and returns a `{overlay}` object shaped exactly like `handleDrawing`'s
 * own `baseLine` state, then clears the pending flag. Returns `null` (a no-op) if nothing is
 * pending, so it's safe to call unconditionally.
 * @param {google.maps.Map} map - The now-ready map instance to draw onto.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingShoreSPage} - Shore-S's result page, via `GoogleMap`'s `onLoad`.
 * @memberof module:ShoreS
 * @returns {{overlay: google.maps.Polyline}|null}
 */
export const restorePendingBaseline = (map) => {
  const raw = window.sessionStorage.getItem(PENDING_BASELINE_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);

  let coords;
  try {
    coords = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(coords) || coords.length < 2) return null;

  const overlay = new window.google.maps.Polyline({
    path: coords.map(([lng, lat]) => ({ lat, lng })),
    strokeColor: '#03d4f0ff',
    strokeOpacity: 1,
    strokeWeight: 3,
  });
  overlay.setMap(map);
  return { overlay };
};

/**
 * @function renderPendingReferenceLayer
 * @description If `importSessionState` left a reference-coastline result waiting, reprojects it
 * back to WGS84 (it's stored/computed in the model's own CRS) and draws it exactly like
 * `handleRun`'s own reference-coastline layer, then clears the pending flag. A no-op if nothing is
 * pending.
 * @param {Function} dispatch - Redux dispatch.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingShoreSPage} - Shore-S's result page, via `GoogleMap`'s `onLoad`.
 * @memberof module:ShoreS
 * @returns {void}
 */
export const renderPendingReferenceLayer = (dispatch) => {
  const raw = window.sessionStorage.getItem(PENDING_REFERENCE_STORAGE_KEY);
  if (!raw) return;
  window.sessionStorage.removeItem(PENDING_REFERENCE_STORAGE_KEY);

  const { referenceCoastlinesGeoJSON, crs } = JSON.parse(raw);
  const referenceWGS84 = reprojectFeatureCollection(referenceCoastlinesGeoJSON, crs, 'EPSG:4326');
  const displayFeatures = toDisplayFeatures(referenceWGS84.features);

  dispatch(Map.removeShapeGroup(REFERENCE_COASTLINE_GROUP));
  const shapes = displayFeatures
    .map((feature) => MapUtils.addShape(feature, REFERENCE_COLOR, 1, REFERENCE_COASTLINE_GROUP))
    .filter(Boolean);
  if (!shapes.length) return;
  dispatch(Map.addShapes(shapes, 'forms.shoreS.map.legendReference', REFERENCE_COASTLINE_GROUP, displayFeatures));
};
